/**
 * Visibility Utilities for FormCrafter
 * Functions for evaluating field visibility conditions
 */

import { VISIBILITY_OPERATORS } from '../../constants/formCrafter.constant';

/**
 * Evaluates a single visibility condition
 * @param {any} sourceValue - The value from the source field
 * @param {string} operator - The comparison operator
 * @param {any} targetValue - The target value to compare against
 * @returns {boolean}
 */
/**
 * Normalizes a value for comparison by converting to lowercase and removing spaces
 * This handles cases like "option1" vs "Option 1"
 */
const normalizeForComparison = (value) => {
  if (value === null || value === undefined) return '';
  return String(value).toLowerCase().replace(/\s+/g, '');
};

export const evaluateCondition = (sourceValue, operator, targetValue) => {
  // Multi-select / checkbox answers are arrays: match against each selected value
  if (Array.isArray(sourceValue)) {
    switch (operator) {
      case VISIBILITY_OPERATORS.EQUALS:
      case VISIBILITY_OPERATORS.CONTAINS:
        return sourceValue.some((item) =>
          evaluateCondition(item, VISIBILITY_OPERATORS.EQUALS, targetValue)
        );
      case VISIBILITY_OPERATORS.NOT_EQUALS:
      case VISIBILITY_OPERATORS.NOT_CONTAINS:
        return !sourceValue.some((item) =>
          evaluateCondition(item, VISIBILITY_OPERATORS.EQUALS, targetValue)
        );
      case VISIBILITY_OPERATORS.IS_EMPTY:
        return sourceValue.length === 0;
      case VISIBILITY_OPERATORS.IS_NOT_EMPTY:
        return sourceValue.length > 0;
      default:
        break;
    }
  }
  switch (operator) {
    case VISIBILITY_OPERATORS.EQUALS:
      // First try loose equality for type coercion (e.g., number 5 vs string "5")
      // Then try normalized comparison for string format differences (e.g., "option1" vs "Option 1")
      return (
        // eslint-disable-next-line eqeqeq
        sourceValue == targetValue ||
        normalizeForComparison(sourceValue) ===
          normalizeForComparison(targetValue)
      );
    case VISIBILITY_OPERATORS.NOT_EQUALS:
      // Use loose inequality and normalized comparison
      return (
        // eslint-disable-next-line eqeqeq
        sourceValue != targetValue &&
        normalizeForComparison(sourceValue) !==
          normalizeForComparison(targetValue)
      );
    case VISIBILITY_OPERATORS.CONTAINS:
      return String(sourceValue || '').includes(String(targetValue));
    case VISIBILITY_OPERATORS.NOT_CONTAINS:
      return !String(sourceValue || '').includes(String(targetValue));
    case VISIBILITY_OPERATORS.IS_EMPTY:
      return !sourceValue || sourceValue === '';
    case VISIBILITY_OPERATORS.IS_NOT_EMPTY:
      return sourceValue && sourceValue !== '';
    default:
      return true;
  }
};

/**
 * Evaluates field visibility based on conditions and form data
 * @param {Object} field - The field object with optional conditions array
 * @param {Object} formData - Current form data { fieldId: value }
 * @returns {boolean} - Whether the field should be visible
 */
export const isFieldVisible = (field, formData) => {
  if (
    !field.conditions ||
    !Array.isArray(field.conditions) ||
    field.conditions.length === 0
  ) {
    return true;
  }

  // All conditions must be met (AND logic)
  return field.conditions.every((condition) => {
    const sourceValue = formData?.[condition.sourceFieldId];
    return evaluateCondition(sourceValue, condition.operator, condition.value);
  });
};
